import { OnInit, Directive } from '@angular/core';
import { Inject, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BaseDestroyable } from '@core/base-destroyable';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppConfigService } from 'app/services/appConfig/appConfigService';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { FeatureToggle, SoftwareOptionsForCompany } from '@shared/enums';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { AccessibilityService } from '@shared/accessibility.service';
import { TimberService } from '@logging/timber.service';
import { ShellContextService } from 'app/services/shell-context/shell-context.service';
import { IKeyValuePair } from '@interfaces/IKeyValuePair';
import { combineLatest } from 'rxjs';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { NavigationEnd, Router } from '@nodeModules/@angular/router';
import isEqual from 'lodash/isEqual';
import { AuthService } from 'app/services/authentication/auth.service';
@Directive({
	selector: 'ada-chat-bot',
})
export class AdaChatBotDirective extends BaseDestroyable implements OnInit {
	window: any;
	contactId: number;
	companyId: number;
	private latestContext: [number, number] | null = null;

	constructor(
		public globalSettingsService: GlobalSettingsService,
		public renderer: Renderer2,
		@Inject(DOCUMENT) private document: Document,
		protected appConfigService: AppConfigService,
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		public softwareOptionsService: SoftwareOptionsService,
		private accessibilityService: AccessibilityService,
		private shellContextService: ShellContextService,
		private router: Router,
		private timberService: TimberService,
		private authService: AuthService,
	) {
		super();
		this.window = <any>this.document.defaultView;
	}

	get username(): string {
		const settings = this.globalSettingsService.get();
		const selectedCompany = settings.companies.filter((company) => company.id === settings.selectedCompanyId)[0];
		const selectedContact = selectedCompany?.contacts
			? selectedCompany.contacts.filter((contact) => contact.contactId === settings.selectedDoctorId)[0].contactName
			: settings.username;
		return selectedContact;
	}

	ngOnInit(): void {
		const flags$ = this.featuresToggleSettingsService.getFeaturesToggleSettings();

		const onHomeRoute$ = this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
			map((event) => event.url === '/doctors/home'),
			startWith(this.router.url === '/doctors/home'), // in case already on the route
			distinctUntilChanged()
		);

		const context$ = combineLatest([
			this.shellContextService.selectedCompanyId$,
			this.shellContextService.selectedContactId$,
		  ]).pipe(
			shareReplay(1),
			map(([companyId, contactId]) => [companyId, contactId] as [number, number]),
			distinctUntilChanged((a, b) => {
			  const eq = isEqual(a, b);
			  return eq;
			})
		  );

		combineLatest([onHomeRoute$, context$, flags$])
			.pipe(
				takeUntil(this.componentAlive$),
				filter(() => this.authService.isAuthenticated()),
				tap(() => this.showHideAdaChatBot()),
				filter(([isHome]) => isHome),
				filter(([_, ctx]) => {
					const changed = !isEqual(this.latestContext, ctx);
					return changed;
				}),
				tap(([_, [companyId, contactId]]) => {
					const newCtx: [number, number] = [companyId, contactId];
					const prev: [number, number] = this.latestContext;
					if (!isEqual(prev, newCtx)) {
					  this.latestContext = newCtx;
					  this.companyId = companyId;
					  this.contactId = contactId;
					  this.stopAdaChatBot();
					}
				}),
				switchMap(([_, [companyId]]) =>
					this.softwareOptionsService.getCompanySoftwareOptionsKeyValuePair(companyId)
				),
				tap(async (companySwo) => {
					const isAdaFFOrSWOEnabled = this.isAdaChatbotFFOrSWOEnabled(companySwo);
					if (!isAdaFFOrSWOEnabled) {
						await this.stopAdaChatBot();
						return false;
					} else {
						await this.initializeOrUpdateAda();
					}
				})
			)
			.subscribe();
	}

	async initializeOrUpdateAda(): Promise<void> {
		const adaScriptUrl = !!this.appConfigService.appSettings.adaChatBot
			? this.appConfigService.appSettings.adaChatBot.adaScriptUrl
			: null;
		if (!adaScriptUrl) {
			return;
		}
		let isAdaScriptLoaded = await this.isAdaScriptLoaded(adaScriptUrl);

		if (isAdaScriptLoaded) {
			if (this.window?.adaEmbed?.stop) {
				await this.window.adaEmbed.stop();
			}
			await this.startAdaAndApplyVisibility();
			await this.initializeAdaSettings();
		} else {
			let scriptElement = this.renderer.createElement('script');
			scriptElement.type = 'text/javascript';
			scriptElement.src = adaScriptUrl;
			scriptElement.cluster = 'eu';
			scriptElement.id = '__ada';
			scriptElement.setAttribute('data-handle', this.getAdaDataHandle());
			this.renderer.appendChild(this.document.head, scriptElement);

			scriptElement.onload = async () => {
				this.timberService.info('Ada Loaded Succesfully', { module: 'AdaChatBotDirective' });
				this.initializeAdaSettings();
			};

			scriptElement.onerror = () => {
				this.timberService.error('error while loading ada chatbot', { module: 'AdaChatBotDirective' });
			};
		}
	}

	private getAdaDataHandle(): string {
		const useNewHandle = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.UseNewAdaChatbotDataHandle, false);
		return useNewHandle
			? this.appConfigService.appSettings.adaChatBot.adaDataHandle
			: 'align-itero';
	}

	private isAdaChatbotFFOrSWOEnabled(cswo: IKeyValuePair): boolean {
		const chatBotFFEnabled = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.ChatBot, false);
		const chatbotSwoEnabled = Object.entries(cswo).some(([key]) => cswo[key] === SoftwareOptionsForCompany.ChatBot);
		return chatBotFFEnabled || chatbotSwoEnabled;
	}

	private async isAdaScriptLoaded(adaScriptUrl): Promise<boolean> {
		let scripts = await document.getElementsByTagName('script');
		for (let i = 0; i < scripts.length; i++) {
			let scriptElement = scripts[i];
			let adaUrlOrigin;

			const scriptSourcePath = await scriptElement.getAttribute('src');
			try {
				adaUrlOrigin = new URL(adaScriptUrl).origin;
			} catch (error) {
				this.timberService.error('invalid or missing ada chatbot url', { module: 'AdaChatBotDirective', error: error });
			}
			const scriptHost = adaUrlOrigin;
			if (scriptSourcePath) {
				if (scriptSourcePath.includes(scriptHost)) {
					return true;
				}
			}
		}
		return false;
	}

	private async startAdaAndApplyVisibility(): Promise<void> {
		await this.startAda();
		this.showHideAdaChatBot();
	}

	private async startAda(): Promise<void> {
		try {
			await this.window.adaEmbed.start({
				handle: this.getAdaDataHandle(),
				language: 'en',
			});
		} catch (error) {
			this.timberService.error('Error starting Ada', { module: 'AdaChatBotDirective' });
		}
	}

	private async initializeAdaSettings(): Promise<void> {
		await this.window.adaEmbed.setMetaFields({
			username: this.username,
			companyId: this.companyId,
			matId: this.contactId,
		});
	}

	private showHideAdaChatBot(): void {
		const isAdaChatbotIconVisible = document.querySelector('#ada-entry');
		if (!isAdaChatbotIconVisible) {
			return;
		}
		if (this.accessibilityService.getPathName() === '/doctors/home') {
			isAdaChatbotIconVisible.setAttribute('style', 'display:contents');
		} else {
			isAdaChatbotIconVisible.setAttribute('style', 'display:none');
		}
	}

	private async stopAdaChatBot(): Promise<void> {
		const isStarted = await this.window?.adaEmbed?.isStarted?.();
		if (isStarted) {
			try {
				await this.window.adaEmbed.stop();
				await this.window.adaEmbed.reset();
			} catch (err) {
				this.timberService.warn('Error stopping Ada', { module: 'AdaChatBotDirective' });
			}
		}
		// Hide Ada icon from UI if it’s still around
		const adaChatBotVisible = document.querySelector('#ada-entry');
		if (adaChatBotVisible) {
			adaChatBotVisible.setAttribute('style', 'display:none');
		}
	}
}